import { andList, formsPhrase } from './pageChecks';

/**
 * Today: approvals that depend on each other ("tinycms UX optimization review
 * v12"). A page that places a form also waiting can't go live first, so the
 * two are one card: "Needs the Tips form (also waiting)", approved together.
 * Several pages sharing forms the agent drafted with them become one card too:
 * "Your agent drafted 3 pages and 2 forms", with Review all.
 */

export interface GroupMember {
  kind: 'page' | 'form';
  id: string;
  title: string | null;
  live: boolean;
  workingHash: string;
}

export interface GroupWords {
  title: string;
  chip: string;
  preview: string;
  note: string;
  cta: string;
  reviewLabel: string;
  confirm: string;
  done: string;
}

const count = (n: number, one: string) => `${n} ${one}${n === 1 ? '' : 's'}`;
const named = (m: GroupMember) => m.title || (m.kind === 'form' ? 'Untitled' : 'Untitled page');

/** What a group's card says, from its pages and forms (in Today's order). */
export function groupWords(pages: GroupMember[], forms: GroupMember[]): GroupWords {
  const what = formsPhrase(forms.map((f) => ({ name: named(f) })));
  const also =
    forms.length === 1 ? 'also waiting' : forms.length === 2 ? 'both also waiting' : 'all waiting';
  const items = `${count(pages.length, 'page')} and ${count(forms.length, 'form')}`;
  const done = `Published ${pages.length === 1 ? `“${named(pages[0])}”` : count(pages.length, 'page')} and ${what}.`;
  if (pages.length === 1) {
    const page = pages[0];
    return {
      title: named(page),
      chip: page.live ? 'Page change' : 'New page',
      preview: page.title || 'page preview',
      note: `Needs ${what} (${also}). They go live together.`,
      cta: forms.length === 1 ? 'Approve page + form' : `Approve page + ${forms.length} forms`,
      reviewLabel: forms.length === 1 ? 'Review both' : 'Review all',
      confirm: `Approve and publish “${named(page)}” and ${what} now?`,
      done,
    };
  }
  const allNew = [...pages, ...forms].every((m) => !m.live);
  return {
    title: allNew ? `Your agent drafted ${items}` : `${items} go live together`,
    chip: allNew ? 'New pages' : 'Page changes',
    preview: items,
    note: `${andList(pages.map(named))}, with ${what} they use. They go live together.`,
    cta: `Approve all ${pages.length + forms.length}`,
    reviewLabel: 'Review all',
    confirm: `Approve and publish ${items} now? They go live together.`,
    done,
  };
}
